/*
 * Copyright (c) Domotz Ltd. All rights reserved.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import intl from 'react-intl-universal';
import { Area, AreaChart, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

import './OutageView.css';

import AppNavbar from '../InternetNavbar';
import AppFooter from '../../components/AppFooter';
import NotFound from "../../components/NotFound";
import Loading from "../../components/Loading";

import { renderMetaTags } from "../../helpers/MetaTagsHelper";
import { formatDuration, formatSmartAbsoluteDate } from '../../helpers/DateHelper';
import { formatPercentage } from '../../helpers/JsHelper';
import SignUpPromoSectionWhite from "../../components/SignUpPromoSectionWhite";

import { linkForPlace } from "../LinkHelper";
import { getCountryName } from "../place/GeoCountries";
import OutageSeverityIndicator from "../isp/OutageSeverityIndicator";
import IspSupportSummary from "../isp/IspSupportSummary";

import OutagePlaceList from "./OutagePlaceList";
import OutageRelatedList from "./OutageRelatedList";
import OutageMap from "./OutageMap";


class OutageView extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            item: {},
            isp: {},
            isLoading: true,
            isFound: false,
            forcedTz: null
        };
    }


    validateRestResponse(response) {
        if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(response) {
        this.setState({ item: {}, isp: {}, isLoading: false, isFound: false });
    }

    componentDidMount() {

        const isUTC = (this.props.match.params.tz && this.props.match.params.tz.toLowerCase() === "utc");
        this.setState({ isLoading: true, isFound: false, forcedTz: isUTC ? 'UTC' : null });

        const reportId = this.props.match.params.reportId;

        const url = '/rest/internet/outage/' + reportId + "?clientTzOffset=" + (new Date().getTimezoneOffset());

        window.scrollTo(0, 0);

        fetch(url)
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                this.setState({ item: data });

                if (data.active) { // auto refresh if active...
                    setTimeout(function () {
                        window.location.reload();
                    }, 300000);
                }

                if (data.mainDimensionIspValue && data.mainDimensionCountryValue) {
                    let url = '/rest/internet/isp/' + data.mainDimensionIspValue +
                        '/' + data.mainDimensionCountryValue +
                        '/' + (data.mainDimensionRegionValue ? data.mainDimensionRegionValue : 'all');

                    fetch(url)
                        .then(response => this.validateRestResponse(response))
                        .then(response => response.json())
                        .then(data => {
                            this.setState({ isp: data, isLoading: false, isFound: true });
                        })
                        .catch(e => {
                            this.setState({ isLoading: false, isFound: true });
                        })

                } else {
                    this.setState({ isLoading: false, isFound: true });
                }
            })
            .catch(e => this.catchRestError(e))
            ;
    }


    renderChart() {
        const { item, forcedTz } = this.state;
        const points = item.chartPoints;

        const data = points.map((point, i) => {

            let toReturn;

            if (point.reportSeverityNum <= 0 && ((i > 0 && points[i - 1].reportSeverityNum === 0) || (i < points.length - 1 && (points[i + 1].reportSeverityNum === 0)))) {
                toReturn = { meta: "", labelx: point.timestamp, value0: point.impactPerc };
            }
            else if (point.reportSeverityNum <= 1 && ((i > 0 && points[i - 1].reportSeverityNum === 1) || (i < points.length - 1 && (points[i + 1].reportSeverityNum === 1)))) {
                toReturn = { meta: "", labelx: point.timestamp, value0: (point.impactPerc - 0.4), value1: point.impactPerc }
            }
            else if (point.reportSeverityNum <= 2 && ((i > 0 && points[i - 1].reportSeverityNum === 2) || (i < points.length - 1 && (points[i + 1].reportSeverityNum === 2)))) {
                toReturn = { meta: "", labelx: point.timestamp, value1: (point.impactPerc - 0.4), value2: point.impactPerc }
            }
            else if (point.reportSeverityNum <= 3 && ((i > 0 && points[i - 1].reportSeverityNum === 3) || (i < points.length - 1 && (points[i + 1].reportSeverityNum === 3)))) {
                toReturn = { meta: "", labelx: point.timestamp, value2: (point.impactPerc - 0.4), value3: point.impactPerc }
            }
            else if (point.reportSeverityNum <= 4 && ((i > 0 && points[i - 1].reportSeverityNum === 4) || (i < points.length - 1 && (points[i + 1].reportSeverityNum === 4)))) {
                toReturn = { meta: "", labelx: point.timestamp, value3: (point.impactPerc - 0.4), value4: point.impactPerc }
            }
            else if (point.reportSeverityNum <= 5 && ((i > 0 && points[i - 1].reportSeverityNum === 5) || (i < points.length - 1 && (points[i + 1].reportSeverityNum === 5)))) {
                toReturn = { meta: "", labelx: point.timestamp, value4: (point.impactPerc - 0.4), value5: point.impactPerc }
            } else {
                toReturn = { meta: "", labelx: point.timestamp, value0: null, value1: null, value2: null, value3: null, value4: null, value5: null }
            }

            let c = 0;
            while (c <= 5) {
                if (!toReturn["value" + c]) toReturn["value" + c] = 0;
                c++;
            }

            return toReturn;
        });

        let timeOptions = { hour: 'numeric', minute: 'numeric' };
        if (forcedTz) {
            timeOptions.timeZone = forcedTz;
            timeOptions.timeZoneName = 'short';
        }

        const skipXLabel = (value, index) => {

            if (index % 20 === 0) {
                let date = new Date(value);
                return date.toLocaleTimeString("en-US", timeOptions);
            } else {
                return null;
            }
        }

        let countFormatter = 0;
        const formatterX = (v) => {

            const value = skipXLabel(v, countFormatter);
            countFormatter++;
            if (value) {
                return value;
            }
            return "";
        }

        const formatterY = (value) => {
            if (Math.round(value) === value) {
                return value + '%';
            }
            return Number.parseFloat(value).toFixed(1) + '%';
        }

        const formatterTooltip = (value, name, props) => {
            let payload = props.payload;

            if (payload.value5 !== 0) {

                if (name === "value5") return [parseFloat(value).toFixed(2) + "%", "Drop rate"];

                return [null, null, null];

            } else if (payload.value4 !== 0) {

                if (name === "value4") return [parseFloat(value).toFixed(2) + "%", "Drop rate"];

                return [null, null, null];

            } else if (payload.value3 !== 0) {

                if (name === "value3") return [parseFloat(value).toFixed(2) + "%", "Drop rate"];

                return [null, null, null];

            } else if (payload.value2 !== 0) {

                if (name === "value2") return [parseFloat(value).toFixed(2) + "%", "Drop rate"];

                return [null, null, null];

            } else if (payload.value1 !== 0) {

                if (name === "value1") return [parseFloat(value).toFixed(2) + "%", "Drop rate"];

                return [null, null, null];

            } else if (payload.value0 !== 0) {

                if (name === "value0") return [parseFloat(value).toFixed(2) + "%", "Drop rate"];

                return [null, null, null];

            } else {

                return [null, null, null];
            }
        }

        const formatterLabelTooltip = (value) => {
            let date = new Date(value);
            return date.toLocaleTimeString("en-US", timeOptions);
        }

        let max = 0;
        points.forEach((point) => max = Math.max(point.impactPerc, max));
        if (max > 50) max = 100;
        else if (max > 15) max = 50;
        else if (max < 5) max = 10;
        else max = 25;

        console.log('MAX ', max)

        return <AreaChart className="small" width={1000} height={180} data={data}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <XAxis dataKey="labelx" tickFormatter={formatterX} tickLine={false} axisLine={false} />
            <YAxis domain={['dataMin', 10]} tickFormatter={formatterY} tickLine={false} axisLine={false} />
            <CartesianGrid strokeDasharray="3 3" horizontalPoints={[0, 90]} verticalPoints={[100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]} />
            <Tooltip formatter={formatterTooltip} labelFormatter={formatterLabelTooltip} contentStyle={{ "border-radius": "10px", "maxHeight": "100px" }} />
            <Area type="basis" dataKey="value0" stroke="none" fillOpacity={1} fill="#82ca9d" />
            <Area type="monotone" dataKey="value1" stroke="none" fillOpacity={1} fill="#FFE666" />
            <Area type="monotone" dataKey="value2" stroke="none" fillOpacity={1} fill="#FFBB33" />
            <Area type="monotone" dataKey="value3" stroke="none" fillOpacity={1} fill="#FF8000" />
            <Area type="monotone" dataKey="value4" stroke="none" fillOpacity={1} fill="#FF5500" />
            <Area type="monotone" dataKey="value5" stroke="none" fillOpacity={1} fill="#FF2A00" />
        </AreaChart>

    }

    onDrawHandler(context) {
        if (context.type === 'area') {
            context.element.attr({
                // Now we set the style attribute on our bar to override the default color of the bar. By using a HSL colour we can easily set the hue of the colour dynamically while keeping the same saturation and lightness. From the context we can also get the current value of the bar. We use that value to calculate a hue between 0 and 100 degree. This will make our bars appear green when close to the maximum and red when close to zero.
                style: 'fill: blue;'
            });
        }
    }

    renderChartTitle() {
        const { item, forcedTz } = this.state;

        let peakDate = new Date(item.peakTime);
        let options = { hour: 'numeric', minute: 'numeric' };
        if (forcedTz) {
            options.timeZone = forcedTz;
            options.timeZoneName = 'short';
        }
        let peakTime = peakDate.toLocaleTimeString("en-US", options);

        let impactPerc = formatPercentage(item.impactPerc);

        return [<h2 className="h5 mb-0">{intl.getHTML('outage_peak', { impact: impactPerc, time: peakTime })}</h2>,
        <h3 className="small text-secondary mb-4">{intl.getHTML('outage_timeline', { probes: item.totalProbes })}</h3>];
    }

    renderImpactHeader() {
        const { item } = this.state;

        let impactPerc = formatPercentage(item.impactPerc);

        let parentText = "";
        if (item.parentImpactPerc > 0 && item.mainDimensionIspValue) {
            let place = item.mainDimensionRegionValue ? item.mainDimensionRegionValue : getCountryName(item.mainDimensionCountryValue);
            parentText = intl.get('outage_impact_percentage', { perc: formatPercentage(item.parentImpactPerc), place: place })
        }

        return (
            <h2 className="h5 mb-1">{intl.getHTML('outage_impact_analysis', { impact: impactPerc, parent: parentText })}</h2>
        );
    }

    renderOutageMetaTags() {
        const { item } = this.state;

        let isp = item.mainDimensionIspValue;
        let region = item.mainDimensionRegionValue;
        let country = item.mainDimensionCountryValue;

        let outageSubj = item.mainDimensionIspBestName ? item.mainDimensionIspBestName : (region ? region : getCountryName(country));
        if (isp) {
            outageSubj = outageSubj + " in " + (region ? region : getCountryName(country));
        }
        outageSubj += " on " + item.localFullDateTime;
        let title = "Internet Outage of " + outageSubj;

        let description = item.severity + " Internet Outage of " + outageSubj + ". ";

        if (item.wideAreaImpact) {
            description = "Widespread outage over whole " + (region ? region : getCountryName(country)) + ". ";
        }
        if (item.topDrills && item.topDrills.length > 0) {
            description += "Impact on ";
            for (var i in item.topDrills) {
                if (i > 0) description += ", ";
                description += item.topDrills[i].dimValue;
            }
            description += ". ";
        }
        description += "Outage, internet down with live map and problem chart, down time and peak analysis and recurrence detection.";

        return renderMetaTags(title, description, item.webCardImageUrl);
    }

    renderOutageInfo() {
        const { item, forcedTz } = this.state;

        //let outage = this.outage();

        // let dimLength = item.mainDimensionPath.length;
        // let title = item.mainDimensionPath[dimLength - 1];

        let isp = item.mainDimensionIspValue;
        let region = item.mainDimensionRegionValue;
        let country = item.mainDimensionCountryValue;
        let countryFlag = item.countryFlag;

        let outageSubj = item.mainDimensionIspBestName ? item.mainDimensionIspBestName : (region ? region : getCountryName(country));

        let ispLink = '/provider/' + isp + '/' + getCountryName(country) + (region ? '/' + region : '');
        let placeLink = linkForPlace(getCountryName(country), region);

        let impactScore = "0";
        // let impactClass;
        let severity = "";
        switch (item.severity) {
            case "Minor":
                severity = intl.get('outage_severity_minor');
                impactScore = "20";
                // impactClass += "text-body";
                break;
            case "Moderate":
                severity = intl.get('outage_severity_moderate');
                impactScore = "40";
                // impactClass += "text-body";
                break;
            case "Considerable":
                severity = intl.get('outage_severity_considerable');
                impactScore = "60";
                // impactClass += "text-body";
                break;
            case "Major":
                severity = intl.get('outage_severity_major');
                impactScore = "80";
                // impactClass += "text-danger";
                break;
            case "Critical":
                severity = intl.get('outage_severity_critical');
                impactScore = "100";
                // impactClass += "text-danger";
                break;
            default:
                severity = "";
                impactScore = 0;
                // impactClass += "text-body";
                break;
        }

        // const stateClass = item.active ? "text-danger" : "text-body";

        let startTime = formatSmartAbsoluteDate(item.startTime, forcedTz);

        let diffMsecs = (item.endTime - item.startTime);
        //const stateText = item.active ? intl.get('outage_ongoing_lasting', { startTime: startTime, duration: formatDuration(diffMsecs, false) }) : intl.get('outage_started_lasted', { startTime: startTime, duration: formatDuration(diffMsecs, false) });
        let recurrenceText = "";
        if (item.recurrenceInLast24h > 0 || item.recurrenceDailyCount > 0 || item.recurrenceWeeklyCount > 0) {
            if (item.recurrenceInLast24h > item.recurrenceDailyCount) {
                recurrenceText += intl.get('outage_recurrence_in_24', { count: ordinal(item.recurrenceInLast24h + 1) });
            }
            if (item.recurrenceDailyCount > 0) {
                if (recurrenceText.length > 0) recurrenceText += ", ";
                recurrenceText += intl.get('outage_recurrence_daily', { count: item.recurrenceDailyCount });
            }
            if (item.recurrenceWeeklyCount > 0) {
                if (recurrenceText.length > 0) recurrenceText += ", ";
                recurrenceText += intl.get('outage_recurrence_weekly', { count: item.recurrenceWeeklyCount });
            }
        } else {
            recurrenceText = intl.get('outage_just_once');
        }

        let relatedText = "";
        if (item.ispRelatedLocations.length > 0) {
            for (let i in item.ispRelatedLocations) {
                if (relatedText.length > 0) relatedText += ", ";
                relatedText += item.ispRelatedLocations[i];
            }
        } else if (item.geoRelatedIsps.length > 0) {
            for (let j in item.geoRelatedIsps) {
                if (relatedText.length > 0) relatedText += ", ";
                relatedText += item.geoRelatedIsps[j];
            }
        } else {
            relatedText = intl.get('outage_none');
        }

        let recentBadge, recentBadgeClass;
        if (!item.active) {
            if (item.recent) {
                recentBadge = intl.get('outage_page_badge_recent');
                recentBadgeClass = "badge badge-warning";
            } else {
                recentBadge = intl.get('outage_page_badge_past');
                recentBadgeClass = "badge badge-success";
            }
        }

        return (
            <div>
                <div className="d-md-flex align-items-md-center">
                    <div>
                        {recentBadge &&
                            <h1 className="h4 mb-0">
                                <Link to={ispLink}>
                                    <span className={recentBadgeClass}>{recentBadge}</span>
                                </Link>
                            </h1>
                        }

                        <h1 className="h3 mb-0">{intl.getHTML('outage_page_title', { severity: severity })}
                            <Link to={ispLink} className="link-underline">{outageSubj}</Link>
                        </h1>
                        <ul className="list-inline font-size-1 pb-2">
                            {item.active && <li className="list-inline-item text-muted"><span className="badge badge-danger">{intl.get('outage_ongoing')}</span></li>}
                            <li className="list-inline-item">
                                <OutageSeverityIndicator score={impactScore} />
                            </li>
                            {isp &&
                                <li className="list-inline-item text-muted">•</li>
                            }
                            {isp &&
                                <li className="list-inline-item">
                                    <Link to={ispLink}><span className="fas fa-globe mr-1" />{item.mainDimensionIspBestName}</Link>
                                </li>
                            }
                            <li className="list-inline-item text-muted">•</li>
                            <li className="list-inline-item">
                                <Link to={placeLink}>
                                    <span className="p-1">{countryFlag} </span>
                                    {
                                        region &&
                                        <span>{region}, </span>
                                    }
                                    {getCountryName(country)}
                                </Link>
                            </li>
                            <li className="list-inline-item text-muted">•</li>
                            <li className="list-inline-item">
                                <Link to='/help/what-s-an-outage'>
                                    <span className="fas fa-question-circle mr-1" />
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <span className="ml-auto text-lg-right">
                        <IspSupportSummary isp={this.state.isp} />
                    </span>
                </div>

                <div className="d-md-flex align-items-md-center">
                    <div className="mb-3 mb-md-0">
                        <div className="sm-avatar mr-1">
                            <Link to={ispLink}>
                                <span className="avatar avatar-sm avatar-circle">
                                    <img className="avatar-img img-fluid" src={item.imageUrl} alt="ISP Logo" />
                                </span>
                            </Link>
                        </div>
                    </div>

                    <div className="column-divider column-divider--none-md pl-4 mb-3 mb-md-0 ml-4">
                        <h3 className="small text-secondary mb-0">{intl.get('outage_started')}</h3>
                        <small className="fas fa-calendar-alt text-secondary align-middle mr-1" />
                        <span className="align-middle">{startTime}</span>
                    </div>

                    {item.localTimeZone &&
                        <div className="column-divider column-divider--none-md pl-4 mb-3 mb-md-0 ml-4">
                            <h3 className="small text-secondary mb-0">{intl.get('outage_local')} ({item.localTimeZone})</h3>
                            <small className="fas fa-calendar-alt text-secondary align-middle mr-1" />
                            <span className="align-middle">{item.localStartTime}</span>
                        </div>
                    }

                    <div className="column-divider column-divider--none-md pl-4 mb-3 mb-md-0 ml-4">
                        <h3 className="small text-secondary mb-0">{intl.get('outage_duration')}</h3>
                        <small className="far fa-clock text-secondary align-middle mr-1" />
                        <span className="align-middle">{formatDuration(diffMsecs, false)}</span>
                    </div>

                    {item.rootCause &&
                        <div className="column-divider column-divider--none-md pl-4 mb-3 mb-md-0 ml-4">
                            <h3 className="small text-secondary mb-0">{intl.get('outage_root_cause')}</h3>
                            <small className="fas fa-heartbeat text-secondary align-middle mr-1" />
                            <span className="align-middle">{item.rootCause}</span>
                        </div>
                    }

                    <div className="column-divider column-divider--none-md pl-4 mb-3 mb-md-0 ml-4">
                        <h3 className="small text-secondary mb-0">{intl.get('outage_recurrence')}</h3>
                        <small className="fas fa-sync text-secondary align-middle mr-1" />
                        <span className="align-middle">{recurrenceText}</span>
                    </div>

                    <div className="column-divider column-divider--none-md pl-4 mb-3 mb-md-0 ml-4">
                        <h4 className="small text-secondary mb-0">{intl.get('outage_related')}</h4>
                        <small className="fas fa-link text-secondary align-middle mr-1" />
                        <span className="align-middle">{relatedText === "" ? intl.get('outage_none') : relatedText}</span>
                    </div>

                </div>

                <br />
            </div>
        );
    }


    render() {
        const { item, isLoading, isFound } = this.state;

        if (isLoading) {
            return <Loading />
        }

        if (!isFound) {
            return <NotFound />;
        }

        return (
            <div>
                {this.renderOutageMetaTags()}

                <AppNavbar />

                <main id="content" role="main">

                    <div>
                        <div className="container space-1">

                            <div className="row border-bottom">
                                <div className="col-lg-12 mb-2">
                                    {this.renderOutageInfo()}
                                </div>
                            </div>

                            <div className="row space-top-1">
                                <div className="col-lg-12 mb-2">
                                    {this.renderChartTitle()}
                                    {this.renderChart()}
                                </div>
                            </div>

                        </div>

                        <div className="container space-1">
                            <div className="row">
                                <div className="col-lg-12 mb-2">
                                    {this.renderImpactHeader()}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-8 mb-9 mb-lg-0">
                                    <div className="card h-100 card-bordered p-0">
                                        {this.state.isLoading || <OutageMap outages={[this.state.item]} />}
                                    </div>
                                </div>

                                <div className="col-lg-4">
                                    <div className="card card-bordered">
                                        <div className="card-body p-4">
                                            {item.wideAreaImpact &&
                                                <div><div className="d-flex justify-content-between align-items-center">
                                                    <h4 className="h6 mb-0">{intl.get('outage_wide_impact', {
                                                        place:
                                                            item.mainDimensionRegionValue ? item.mainDimensionRegionValue : getCountryName(item.mainDimensionCountryValue)
                                                    })}</h4>
                                                    <div className="position-relative">
                                                        <small className="fas fa-fw fa-map align-middle mr-2" />
                                                    </div>
                                                </div>
                                                    <hr className="mt-2 mb-4" />
                                                    <OutagePlaceList outage={item} dimension='WideAreaImpact' showScore={true} showList={true} limit={20} />
                                                </div>
                                            }

                                            <div className="d-flex justify-content-between align-items-center">
                                                <h4 className="h6 mb-0">{intl.get('outage_impacted_cities')}</h4>
                                                <div className="position-relative">
                                                    <small className="fas fa-fw fa-building align-middle mr-2" />
                                                </div>
                                            </div>
                                            <hr className="mt-2 mb-4" />
                                            <OutagePlaceList outage={item} dimension='City' showScore={true} showList={true} limit={20} />

                                            {!item.mainDimensionRegionValue &&
                                                <div><div className="d-flex justify-content-between align-items-center">
                                                    <h4 className="h6 mb-0">{intl.get('outage_impacted_regions')}</h4>
                                                    <div className="position-relative">
                                                        <small className="fas fa-fw fa-map align-middle mr-2" />
                                                    </div>
                                                </div>
                                                    <hr className="mt-2 mb-4" />
                                                    <OutagePlaceList outage={item} dimension='Region' showScore={true} showList={true} limit={20} />
                                                </div>
                                            }
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h4 className="h6 mb-0">{intl.get('outage_related_outages')}</h4>
                                                <div className="position-relative">
                                                    <small className="fas fa-fw fa-calendar-times align-middle mr-2" />
                                                </div>
                                            </div>
                                            <hr className="mt-2 mb-4" />
                                            <OutageRelatedList outage={item} showScore={true} showList={true} limit={10} />

                                            {item.impactedAsns &&
                                                <div>
                                                    <h4 className="mb-0 small text-muted text-right">ASN:{item.impactedAsns.map(asn => " " + asn)}</h4>
                                                </div>
                                            }

                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <SignUpPromoSectionWhite />

                    </div>

                </main>


                <AppFooter />
            </div>
        );
    }
}

function ordinal(i) {
    const suffixes = ["th", "st", "nd", "rd", "th", "th", "th", "th", "th", "th"];
    switch (i % 100) {
        case 11:
        case 12:
        case 13:
            return i + "th";
        default:
            return i + suffixes[i % 10];
    }
}


export default OutageView;